import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import { Check, Copy } from 'lucide-react';
import { formatElapsed } from '@/components/practice/practiceFormat.ts';
import { fetchMeetingTranscript } from '@/lib/meetingPage.ts';
import { m } from '@/paraglide/messages.js';
import {
  meetingTranscriptTurns,
  renderMeetingTranscript,
} from './meetingTranscriptTurns.ts';

/* How long "Gekopieerd" stays on the button before it goes back to normal. */
const COPIED_FOR_MS = 2000;

export interface MeetingTranscriptDialogProps {
  open: boolean;
  /** Whose meeting this is — the same user the rest of the page is read for. */
  userId: string;
  meetingId: string;
  onClose: () => void;
}

/**
 * The raw transcript of one meeting, for a superadmin looking into a verslag
 * that does not match what was said.
 *
 * Read only, and read only when it is opened: a transcript is the largest thing
 * a meeting has, and no ordinary visit to this page should carry it. The server
 * answers 404 both to a meeting without one and to anybody who may not read it,
 * so this says the same thing in either case — "geen transcript beschikbaar" —
 * and never explains which of the two it was.
 */
export function MeetingTranscriptDialog({
  open,
  userId,
  meetingId,
  onClose,
}: MeetingTranscriptDialogProps) {
  const [copied, setCopied] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ['meetingTranscript', userId, meetingId],
    queryFn: () => fetchMeetingTranscript(userId, meetingId),
    enabled: open,
  });

  const turns = meetingTranscriptTurns(data?.segments ?? []);
  /* Nothing to read is the same answer as no transcript at all. */
  const nothing = isError || (!isPending && turns.length === 0);

  const copy = () => {
    void navigator.clipboard.writeText(
      renderMeetingTranscript(turns, m.meeting_transcript_unknown_speaker()),
    );
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FOR_MS);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size='lg'>
        <DialogHeader>
          <DialogTitle>{m.meeting_transcript_title()}</DialogTitle>
        </DialogHeader>

        {/*
          The transcript carries the scroll, not the dialog: the title stays put
          and the way out stays reachable however long the conversation was.
        */}
        <div className='max-h-96 overflow-y-auto px-5 py-4'>
          {nothing && (
            <p className='text-sm text-ink-muted'>{m.meeting_transcript_empty()}</p>
          )}

          {!nothing && isPending && (
            <p className='text-sm text-ink-muted'>{m.meeting_transcript_loading()}</p>
          )}

          <ol className='flex flex-col gap-3.5'>
            {turns.map((turn) => (
              <li key={turn.key} className='flex min-w-0 flex-col'>
                <p className='flex items-baseline gap-2'>
                  <span className='text-sm font-semibold text-ink'>
                    {turn.speaker ?? m.meeting_transcript_unknown_speaker()}
                  </span>
                  {turn.start !== null && (
                    <span className='text-xs text-ink-muted'>
                      {formatElapsed(turn.start)}
                    </span>
                  )}
                </p>
                <p className='text-base leading-relaxed text-ink'>{turn.text}</p>
              </li>
            ))}
          </ol>
        </div>

        <DialogFooter>
          {/*
            The whole thing as plain text, because what happens to a transcript
            next is that it gets pasted into a ticket next to the verslag.
          */}
          <Button size='sm' variant='quiet' disabled={turns.length === 0} onClick={copy}>
            {copied ? (
              <Check aria-hidden='true' className='size-4' />
            ) : (
              <Copy aria-hidden='true' className='size-4' />
            )}
            {copied ? m.meeting_transcript_copied() : m.meeting_transcript_copy()}
          </Button>
          <Button size='sm' variant='quiet' onClick={onClose}>
            {m.meeting_transcript_close()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
